import React, { useState, useEffect } from 'react';
import './BookLibrary.css';

const BLOB_BASE_URL = 'https://paiwizsa.blob.core.windows.net/dynabooks';

const BookLibrary = ({ onSelectBook }) => {
  const [books, setBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to truncate text to max length with ellipsis
  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper to check if URL looks like a valid image URL
  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    // Must end with an image extension
    return /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(url);
  };

  useEffect(() => {
    const loadBooks = async () => {
      try {
        // Load the books manifest
        const manifestResponse = await fetch('/data/books.json');
        if (!manifestResponse.ok) {
          throw new Error('Failed to load books manifest');
        }
        const manifest = await manifestResponse.json();

        // Load metadata for each book from remote blob storage using bookId
        const bookPromises = manifest.books.map(async (book) => {
          const metadataUrl = `${BLOB_BASE_URL}/${book.bookId}/paitar.json`;
          try {
            const response = await fetch(metadataUrl);
            if (!response.ok) {
              console.warn(`Failed to load metadata for ${book.bookId} from blob storage`);
              return null;
            }
            const metadata = await response.json();
            return {
              folder: book.folder,
              bookId: book.bookId, // Use bookId for all remote loading
              title: metadata.BookTitle,
              author: metadata.BookAuthor,
              description: metadata.Description,
              infographicUrl: metadata.InfographicUrl,
              blobFolder: metadata.BlobFolder || book.bookId
            };
          } catch (err) {
            console.warn(`Error loading metadata for ${book.bookId}:`, err);
            return null;
          }
        });

        const loadedBooks = (await Promise.all(bookPromises)).filter(b => b !== null);
        setBooks(loadedBooks);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadBooks();
  }, []);

  // Check if search term looks like a blob folder ID (e.g., "whywegetsi-0599cf9d17b1")
  const isBlobFolderId = (term) => {
    // Blob folder IDs typically have format: name-hexstring (e.g., "whywegetsi-0599cf9d17b1")
    return /^[a-z]+-[a-f0-9]{10,}$/i.test(term.trim());
  };

  const handleLoadFromBlob = () => {
    if (onSelectBook && searchTerm.trim()) {
      onSelectBook({ blobFolder: searchTerm.trim() });
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && isBlobFolderId(searchTerm)) {
      handleLoadFromBlob();
    }
  };

  const filteredBooks = books.filter(book => {
    const term = searchTerm.toLowerCase();
    return (
      book.title.toLowerCase().includes(term) ||
      book.author.toLowerCase().includes(term) ||
      book.description.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="book-library">
        <p className="loading-message">Loading books...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="book-library">
        <p className="error-message">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="book-library">
      
      <div className="search-container">
        <input
          type="text"
          placeholder="Search books or enter a puzzle ID (e.g., whywegetsi-0599cf9d17b1)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          className="search-input"
        />
        {searchTerm && (
          <button 
            className="clear-search-btn"
            onClick={() => setSearchTerm('')}
          >
            ✕
          </button>
        )}
      </div>

      <div className="books-grid">
        {filteredBooks.length === 0 ? (
          <div className="no-results-container">
            <p className="no-results">No books found matching "{searchTerm}"</p>
            {isBlobFolderId(searchTerm) && (
              <div className="load-from-cloud">
                <p>This looks like a puzzle ID. Would you like to load it from the cloud?</p>
                <button 
                  className="load-cloud-btn"
                  onClick={handleLoadFromBlob}
                >
                  ☁️ Load "{searchTerm}" from Cloud
                </button>
              </div>
            )}
          </div>
        ) : (
          filteredBooks.map((book, index) => (
            <div 
              key={index} 
              className="book-card"
              onClick={() => onSelectBook && onSelectBook(book)}
            >
              <div className="row">
                <div className="col-4">
                  <div className="book-thumbnail">
                    {isValidImageUrl(book.infographicUrl) ? (
                      <>
                        <img 
                          src={book.infographicUrl} 
                          alt={`${book.title} infographic`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="thumbnail-placeholder" style={{ display: 'none' }}>
                          <span>📚</span>
                        </div>
                      </>
                    ) : (
                      <div className="thumbnail-placeholder" style={{ display: 'flex' }}>
                        <span>📚</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-8">
                  <div className="book-info">
                    <h3 className="book-title">{truncateText(book.title, 50)}</h3>
                    <p className="book-author">by {truncateText(book.author, 50)}</p>
                    <p className="book-description">{book.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BookLibrary;
